import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MessageCircle, Plus, Trash2, Search, Users, Eye, LogOut, UserPlus, Send, RefreshCw, BarChart3, Map, TrendingUp, Cloud, Settings, X, Download, Filter, Target } from "lucide-react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import GroupAnalytics from "../components/GroupAnalytics";
import GroupHeatmap from "../components/GroupHeatmap";
import GroupInsights from "../components/GroupInsights";
import GroupWordCloud from "../components/GroupWordCloud";
import GroupSettingsModal from "../components/GroupSettingsModal";
import MediaPreview from "../components/MediaPreview";
import WhatsAppMessage from "../components/WhatsAppMessage";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../hooks/useConfirm";
import "../global.css";
import api, { contactsAPI, waGroupsAPI } from "../services/api";

const WhatsappGroups = () => {
  const { darkMode } = useTheme();
  const { user, isUsuario } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showConfirm = useConfirm();
  const [groups, setGroups] = useState([]);
  const [instances, setInstances] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "active");
  const [filterRole, setFilterRole] = useState(searchParams.get('role') || "all");
  const [filterInstance, setFilterInstance] = useState(searchParams.get('instancia') || "all");
  const [sortBy, setSortBy] = useState(searchParams.get('ordem') || "name");
  const [filterTag, setFilterTag] = useState(searchParams.get('tag') || "all");
  const [filterCidade, setFilterCidade] = useState(searchParams.get('cidade') || "all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showAnalytics, setShowAnalytics] = useState(() => localStorage.getItem('wagroups_showAnalytics') !== 'false');
  const [activeView, setActiveView] = useState('groups');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [insightsGroupId, setInsightsGroupId] = useState(null);
  const [wordCloudGroupId, setWordCloudGroupId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [groupMessages, setGroupMessages] = useState({ participants: [] });
  const [analytics, setAnalytics] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [groupsMessageCount, setGroupsMessageCount] = useState({});
  const [filterParticipant, setFilterParticipant] = useState("all");
  const [searchMessage, setSearchMessage] = useState("");
  const [showBlocklistModal, setShowBlocklistModal] = useState(false);
  const [blocklist, setBlocklist] = useState([]);
  const [blocklistInput, setBlocklistInput] = useState("");
  const [blocklistNome, setBlocklistNome] = useState("");

  // Categorias e Settings
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [filterCategorias, setFilterCategorias] = useState(() => {
    const cats = searchParams.get('categorias');
    return cats ? JSON.parse(cats) : {};
  });
  const [openCatDropdown, setOpenCatDropdown] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState(searchParams.get('from') || '');
  const [filterDateTo, setFilterDateTo] = useState(searchParams.get('to') || '');
  const [metaGeral, setMetaGeral] = useState(0);
  const [totalContatosUnicos, setTotalContatosUnicos] = useState(0);
  const [contatosReconstruidos, setContatosReconstruidos] = useState(false);
  const [crescimentoUnicos, setCrescimentoUnicos] = useState(null); // { crescimento, inicial, atual, estimado }
  const [gruposCatValores, setGruposCatValores] = useState({}); // { grupoId: [{categoria_id, opcao_id}] }

  // Forms
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [messageText, setMessageText] = useState("");
  const [modalMode, setModalMode] = useState("create"); // "create" ou "add"

  const clienteId = user?.cliente_id;

  // Sincronizar filtros com URL
  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (filterStatus !== 'active') params.status = filterStatus;
    if (filterRole !== 'all') params.role = filterRole;
    if (filterInstance !== 'all') params.instancia = filterInstance;
    if (sortBy !== 'name') params.ordem = sortBy;
    if (filterTag !== 'all') params.tag = filterTag;
    if (filterCidade !== 'all') params.cidade = filterCidade;
    const catAtivos = Object.fromEntries(Object.entries(filterCategorias).filter(([,v]) => v));
    if (Object.keys(catAtivos).length > 0) params.categorias = JSON.stringify(catAtivos);
    if (searchParams.get('export')) params.export = searchParams.get('export');
    if (filterDateFrom) params.from = filterDateFrom;
    if (filterDateTo) params.to = filterDateTo;
    setSearchParams(params, { replace: true });
  }, [searchTerm, filterStatus, filterRole, filterInstance, sortBy, filterTag, filterCidade, filterCategorias, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (clienteId) {
      loadInstances();
      loadAllGroups();
      loadAnalytics(null);
      loadResumo();
      loadCategorias();
      // loadContacts NÃO roda no mount: traz milhares de contatos e só é usado no
      // seletor de responsável. Carregado sob demanda (ver ensureContacts).
    }
  }, [clienteId]);

  // Re-busca os grupos (e portanto recalcula os cards) sempre que QUALQUER filtro muda.
  // Sem isto, os filtros de região/cidade/categoria/etc não afetavam os cards. Debounce
  // curto para a busca por texto não disparar a cada tecla.
  useEffect(() => {
    if (!clienteId) return;
    const t = setTimeout(() => { loadAllGroups(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterRole, filterInstance, filterTag, filterCidade, filterCategorias, filterDateFrom, filterDateTo, searchTerm, sortBy]);

  // Carrega contatos só quando realmente necessário (abrir seletor de responsável)
  const ensureContacts = async () => {
    if (contacts.length > 0) return;
    await loadContacts();
  };

  // Quando um modal que usa contatos abre, garante que a lista está carregada.
  useEffect(() => {
    if (showCreateModal || showContactModal) {
      ensureContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, showContactModal]);

  const loadAllGroups = async () => {
    setLoading(true);
    try {
      // Montar filtros de categoria ativos
      const catFiltrosAtivos = Object.fromEntries(
        Object.entries(filterCategorias).filter(([k, v]) => v && (Array.isArray(v) ? v.length > 0 : true))
      );
      const params = { cliente_id: clienteId, include_inactive: filterStatus !== "active" };
      if (filterStatus === 'inactive') params.only_inactive = true;
      if (Object.keys(catFiltrosAtivos).length > 0) {
        params.categorias = JSON.stringify(catFiltrosAtivos);
      }
      if (filterCidade !== 'all') params.cidade = filterCidade;
      if (filterInstance !== 'all') params.instancia_id = filterInstance;
      if (filterRole !== 'all') params.role = filterRole;
      if (filterTag !== 'all') params.tag = filterTag;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (sortBy) params.sort = sortBy;
      const response = await api.get('/grupos-whatsapp', { params });
      const responseData = response.data || {};
      const gruposData = responseData.groups || (Array.isArray(responseData) ? responseData : []);
      if (responseData.total_contatos_distintos !== undefined) {
        setTotalContatosUnicos(responseData.total_contatos_distintos);
        setContatosReconstruidos(!!responseData.total_reconstruido);
      }

      // Carregar crescimento real (entradas - saídas por grupo, histórico datado)
      try {
        const growthRes = await waGroupsAPI.getEventosPorGrupo(clienteId, { dias: 7 });
        const growthData = growthRes.data || [];
        // growth = variação de TAMANHO real (snapshot). entradas/saidas = rotatividade.
        const growthMap = {};
        for (const h of growthData) {
          growthMap[String(h.grupo_id)] = {
            growth: Number(h.growth) || 0,
            entradas: Number(h.entradas) || 0,
            saidas: Number(h.saidas) || 0,
          };
        }
        for (const g of gruposData) {
          const gd = growthMap[String(g.id)];
          if (gd) {
            g.growth = gd.growth;        // variação de tamanho no período (snapshot)
            g.entradas_7d = gd.entradas; // entradas reais (rotatividade)
            g.saidas_7d = gd.saidas;     // saídas reais (rotatividade)
          } else {
            g.growth = 0;
            g.entradas_7d = 0;
            g.saidas_7d = 0;
          }
        }
      } catch (e) { }

      setGroups(gruposData);

      // Carregar contagem de mensagens de hoje para cada grupo
      loadTodayMessageCounts(gruposData);

      // Carregar blocklist
      try {
        const blRes = await api.get(`/grupos-whatsapp/blocklist/${clienteId}`);
        setBlocklist(blRes.data || []);
      } catch (e) { }
    } catch (error) {
      toast.error('Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  };

  const loadTodayMessageCounts = async (groups) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      const response = await api.get('/conversas/grupo/count-today-batch', {
        params: { cliente_id: clienteId, date: today }
      });
      // response.data é { group_id: count, ... }
      const countsByGroupId = response.data || {};
      const counts = {};
      for (const group of groups) {
        if (group.group_id && countsByGroupId[group.group_id]) {
          counts[group.id] = countsByGroupId[group.group_id];
        } else {
          counts[group.id] = 0;
        }
      }
      setGroupsMessageCount(counts);
    } catch (error) {
      console.error('Erro ao carregar contagem de mensagens:', error);
    }
  };

  const [exportandoCompleto, setExportandoCompleto] = useState(false);
  const [agruparPor, setAgruparPor] = useState('Região');

  const handleExportCompleto = async () => {
    if (!clienteId) return;
    setExportandoCompleto(true);
    try {
      const res = await waGroupsAPI.exportCompleto(clienteId, agruparPor);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dim = String(agruparPor).toLowerCase().replace(/[^a-z0-9]+/g, '_');
      link.download = `relatorio_${dim}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório exportado');
    } catch (e) {
      console.error('Erro ao exportar relatório:', e);
      toast.error('Não foi possível exportar o relatório');
    } finally {
      setExportandoCompleto(false);
    }
  };

  const syncAllGroups = async () => {
    const zApiInstances = instances.filter(i => i.provider === 'z-api');
    if (zApiInstances.length === 0) {
      toast.warning('Nenhuma instância Z-API encontrada');
      return;
    }

    setSyncing(true);
    let totalSynced = 0;
    let totalNew = 0;

    try {
      for (const inst of zApiInstances) {
        try {
          const result = await api.post('/grupos-whatsapp/sync', {
            instanceId: inst.id,
            clienteId
          });
          totalSynced += result.data.synced || 0;
          totalNew += result.data.new || 0;
        } catch (error) {
          console.error(`Erro ao sincronizar ${inst.nome_instancia}:`, error);
        }
      }

      toast.success(`${totalSynced} grupos sincronizados, ${totalNew} novos`);
      loadAllGroups();
    } catch (error) {
      toast.error('Erro ao sincronizar grupos');
    } finally {
      setSyncing(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const res = await waGroupsAPI.getCategorias();
      setCategorias(res.data?.data || []);
    } catch (e) { }
  };

  const loadInstances = async () => {
    try {
      const response = await api.get('/instancias-whatsapp', {
        params: { cliente_id: clienteId }
      });
      const zApiInstances = (response.data || []).filter(i => i.provider === 'z-api');
      setInstances(zApiInstances);
    } catch (error) {
      toast.error('Erro ao carregar instâncias');
    }
  };

  const loadResumo = async () => {
    try {
      const response = await waGroupsAPI.resumo(clienteId);
      setResumo(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    }
    try {
      const metaRes = await waGroupsAPI.getMetaGeral(clienteId);
      setMetaGeral(metaRes.data?.meta_geral || 0);
      setTotalContatosUnicos(metaRes.data?.total_contatos_unicos || 0);
    } catch (e) { }
  };

  // Crescimento de CONTATOS ÚNICOS respeitando os filtros da página.
  // Recebe os grupos já filtrados (para o subconjunto) + range de data.
  const loadCrescimentoUnicos = async (gruposFiltrados) => {
    try {
      const params = {};
      if (filterDateFrom) params.from = filterDateFrom;
      if (filterDateTo) params.to = filterDateTo;
      if (!filterDateFrom && !filterDateTo) params.dias = 7;
      // Se há qualquer filtro de segmentação ativo, manda os ids dos grupos filtrados
      const temFiltroSegmentacao =
        filterCidade !== 'all' || filterInstance !== 'all' || filterTag !== 'all' ||
        filterRole !== 'all' || (searchTerm && searchTerm.trim()) ||
        Object.values(filterCategorias || {}).some(Boolean);
      if (temFiltroSegmentacao && Array.isArray(gruposFiltrados) && gruposFiltrados.length > 0) {
        params.grupo_ids = gruposFiltrados.map(g => g.id).join(',');
      }
      const uniRes = await waGroupsAPI.getContatosUnicosSerie(clienteId, params);
      setCrescimentoUnicos(uniRes.data || null);
    } catch (e) { }
  };

  // Recalcula o crescimento de únicos sempre que os grupos filtrados mudam
  // (o loadAllGroups já reaplica os filtros da página e atualiza `groups`).
  useEffect(() => {
    if (clienteId) {
      loadCrescimentoUnicos(groups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, filterDateFrom, filterDateTo]);

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.list(clienteId);
      setContacts(response.data.messages || []);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Digite o nome do grupo');
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um contato');
      return;
    }
    if (!filterInstance || filterInstance === 'all') {
      toast.error('Selecione uma instância para criar o grupo');
      return;
    }
    try {
      const phones = selectedContacts.map(contactId => {
        const contact = contacts.find(c => c.id === contactId);
        return contact?.telefone;
      }).filter(Boolean);

      await api.post('/grupos-whatsapp/create', {
        instanceId: filterInstance,
        clienteId,
        name: newGroupName,
        phones
      });
      toast.success('Grupo criado com sucesso');
      setShowCreateModal(false);
      setNewGroupName("");
      setSelectedContacts([]);
      loadAllGroups();
    } catch (error) {
      toast.error('Erro ao criar grupo');
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }
    try {
      await api.post(`/grupos-whatsapp/${selectedGroup.id}/send-message`, {
        clienteId,
        message: messageText
      });
      toast.success('Mensagem enviada');
      setShowMessageModal(false);
      setMessageText("");
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    }
  };

  const addParticipants = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um contato');
      return;
    }
    try {
      const phones = selectedContacts.map(contactId => {
        const contact = contacts.find(c => c.id === contactId);
        return contact?.telefone;
      }).filter(Boolean);

      await api.post(`/grupos-whatsapp/${selectedGroup.id}/participants`, {
        clienteId,
        phones
      });
      toast.success('Participantes adicionados');
      setShowParticipantsModal(false);
      setShowContactModal(false);
      setSelectedContacts([]);
      loadAllGroups();
    } catch (error) {
      toast.error('Erro ao adicionar participantes');
    }
  };

  const leaveGroup = async (group) => {
    showConfirm({
      title: 'Sair do Grupo',
      message: `Deseja sair do grupo "${group.nome}"?`,
      confirmText: 'Sair',
      type: 'warning',
      onConfirm: async () => {
        try {
          await api.post(`/grupos-whatsapp/${group.id}/leave`, { clienteId });
          toast.success('Saiu do grupo');
          loadAllGroups();
        } catch (error) {
          toast.error('Erro ao sair do grupo');
        }
      }
    });
  };

  const viewDetails = async (group) => {
    setGroupDetails(null);
    setShowDetailsModal(true);
    try {
      const response = await api.get(`/grupos-whatsapp/${group.id}`, {
        params: { cliente_id: clienteId }
      });
      setGroupDetails(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
      setShowDetailsModal(false);
    }
  };

  const viewHistory = async (group) => {
    setSelectedGroup(group);
    setShowHistoryModal(true);
    setMessagesPage(1);
    setFilterParticipant("all");
    setSearchMessage("");
    loadGroupMessages(group, 1);
  };

  const loadAnalytics = async (group, days = 7) => {
    try {
      const params = {
        cliente_id: clienteId,
        days
      };

      // Se group for null, busca analytics geral (todos os grupos)
      if (group) {
        params.group_id = group.group_id;
      }

      // Filtrar por instância se selecionada
      if (filterInstance && filterInstance !== 'all') {
        params.instance_id = filterInstance;
      }

      const response = await api.get('/conversas/grupo/analytics', { params });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    }
  };

  const loadGroupMessages = async (group, page = 1, participant = "all", search = "") => {
    setLoadingMessages(true);
    try {
      const params = {
        cliente_id: clienteId,
        group_id: group.group_id,
        page,
        limit: 50
      };

      if (participant !== "all") params.participant_phone = participant;
      if (search) params.search = search;

      const response = await api.get('/conversas/grupo', { params });
      setGroupMessages(response.data);
      setTotalMessages(response.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoadingMessages(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await api.get('/conversas/grupo/export', {
        params: {
          cliente_id: clienteId,
          group_id: selectedGroup.group_id,
          format: 'csv',
          participant_phone: filterParticipant !== "all" ? filterParticipant : undefined,
          search: searchMessage || undefined
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedGroup.nome}_mensagens.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar CSV');
    }
  };

  const exportParticipantesCSV = async (grupo) => {
    const g = grupo || selectedGroup;
    if (!g) return;
    try {
      const response = await api.get(`/grupos-whatsapp/${g.id}/export-csv`, {
        params: { cliente_id: clienteId },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participantes_${g.nome?.replace(/[^a-zA-Z0-9]/g, '_') || 'grupo'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Participantes exportados!');
    } catch (error) {
      toast.error('Erro ao exportar participantes');
    }
  };

  const exportToJSON = async () => {
    try {
      const response = await api.get('/conversas/grupo/export', {
        params: {
          cliente_id: clienteId,
          group_id: selectedGroup.group_id,
          format: 'json',
          participant_phone: filterParticipant !== "all" ? filterParticipant : undefined,
          search: searchMessage || undefined
        }
      });

      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedGroup.nome}_mensagens.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('JSON exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar JSON');
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const selectAllContacts = () => {
    const filteredIds = filteredContactsForModal.map(c => c.id);
    setSelectedContacts(filteredIds);
  };

  const clearSelection = () => {
    setSelectedContacts([]);
  };

  const filteredGroups = groups;

  // Paginação
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGroups = filteredGroups.slice(startIndex, startIndex + itemsPerPage);

  // Reset página quando busca executa
  useEffect(() => {
    setCurrentPage(1);
  }, [groups]);

  const filteredContactsForModal = contacts.filter(contact =>
    contact.nome?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
    contact.telefone?.includes(contactSearchTerm)
  );

  const getInstanceName = (group) => {
    const instance = instances.find(i => i.id === group.instancia_id);
    const nome = instance?.nome_instancia || 'Instância';
    const numero = group.phone_number;

    if (numero) {
      return `${nome} • ${numero}`;
    }
    return nome;
  };

  const openCreateModal = () => {
    setModalMode("create");
    setNewGroupName("");
    setSelectedContacts([]);
    setShowCreateModal(true);
  };

  const openAddParticipantsModal = (group) => {
    setModalMode("add");
    setSelectedGroup(group);
    setSelectedContacts([]);
    setContactSearchTerm("");
    setShowContactModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-nomoz-orange"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout expanded>
      <div className="space-y-6">
        <PageHeader title="Grupos do WhatsApp" subtitle="Gerencie todos os seus grupos do WhatsApp">
          <button
            onClick={() => navigate('/metas')}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border text-sm font-medium rounded-md transition-colors ${darkMode
                ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            <Target className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Metas</span>
          </button>
          <button
            onClick={() => setActiveView(v => v === 'heatmap' ? 'groups' : 'heatmap')}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border text-sm font-medium rounded-md transition-colors ${activeView === 'heatmap'
                ? 'bg-nomoz-orange text-white border-nomoz-orange'
                : darkMode
                  ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            <Map className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Mapa de Temperatura</span>
          </button>
          <button
            onClick={() => { const v = !showAnalytics; setShowAnalytics(v); localStorage.setItem('wagroups_showAnalytics', v); }}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border text-sm font-medium rounded-md transition-colors ${darkMode
                ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            <BarChart3 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{showAnalytics ? 'Ocultar' : 'Mostrar'} Analytics</span>
          </button>
          <button
            onClick={() => setShowBlocklistModal(true)}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border text-sm font-medium rounded-md transition-colors ${darkMode
                ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            🚫
            <span className="hidden sm:inline ml-2">Blocklist</span>
          </button>
          <button
            onClick={syncAllGroups}
            disabled={syncing || instances.length === 0}
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${darkMode
                ? "border-gray-600 text-gray-300 hover:bg-zinc-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
          </button>
          <select
            value={agruparPor}
            onChange={(e) => setAgruparPor(e.target.value)}
            title="Agrupar o relatório por"
            className={`px-2 py-1.5 sm:px-3 sm:py-2 text-sm rounded-md border ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
          >
            <option value="Região">Região</option>
            <option value="cidade">Cidade</option>
            <option value="Zona">Zona</option>
            <option value="Microrregião">Microrregião</option>
            <option value="Tipo">Tipo</option>
            <option value="Dobrada">Dobrada</option>
          </select>
          <button
            onClick={handleExportCompleto}
            disabled={exportandoCompleto}
            title="Gera uma linha por valor da categoria escolhida (Grupos, Contatos Únicos, Crescimento 7d e 24h)."
            className={`inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${darkMode
                ? "bg-emerald-600 text-white hover:bg-emerald-500"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
          >
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{exportandoCompleto ? 'Exportando...' : 'Exportar relatório (CSV)'}</span>
          </button>
        </PageHeader>

        {/* Mapa de Temperatura */}
        {activeView === 'heatmap' ? (
          <GroupHeatmap />
        ) : (
          <>
            {/* Meta Geral de Contatos */}
            {/* Analytics Geral */}
            {showAnalytics && <GroupAnalytics analytics={analytics} darkMode={darkMode} groups={filteredGroups} resumo={resumo} totalContatosUnicos={totalContatosUnicos} contatosReconstruidos={contatosReconstruidos} crescimentoUnicos={crescimentoUnicos} metaGeral={metaGeral} showExport={searchParams.get('export') === 'true'} />}



            {/* Filtros */}
            <div className={`p-4 rounded-lg border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex gap-3">
                <Filter className={`w-5 h-5 flex-shrink-0 mt-2.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                <div className="flex-1 flex flex-wrap items-center gap-3 [&>select]:w-[200px] [&>input]:w-[200px]">

                {/* Busca */}
                <div className="relative w-[416px]">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    placeholder="Buscar por nome, tag, cidade..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>

                {/* Status */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="active">Ativos</option>
                  <option value="all">Todos</option>
                  <option value="inactive">Inativos</option>
                </select>

                {/* Papel */}
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="all">Todos papéis</option>
                  <option value="admin">Admin</option>
                  <option value="member">Membro</option>
                </select>

                {/* Instância */}
                {instances.filter(i => i.provider === 'z-api').length > 1 && (
                  <select
                    value={filterInstance}
                    onChange={(e) => setFilterInstance(e.target.value)}
                    className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option value="all">Todas instâncias</option>
                    {instances.filter(i => i.provider === 'z-api').map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.nome_instancia}</option>
                    ))}
                  </select>
                )}

                {/* Data De */}
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className={`px-2 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />

                {/* Data Até */}
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className={`px-2 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />

                {/* Tag */}
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="all">Todas tags</option>
                  {[...new Set(groups.flatMap(g => g.tags || []))].sort().map(tag => (
                    <option key={tag} value={tag}>#{tag}</option>
                  ))}
                </select>

                {/* Cidade */}
                <select
                  value={filterCidade}
                  onChange={(e) => setFilterCidade(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="all">Todas cidades</option>
                  <option value="__none__">Sem classificação</option>
                  {[...new Set(groups.map(g => g.cidade).filter(Boolean))].sort().map(cidade => (
                    <option key={cidade} value={cidade}>{cidade}</option>
                  ))}
                </select>

                {/* Ordenação */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="name">Ordenar: Nome</option>
                  <option value="participants">Ordenar: Participantes</option>
                  <option value="recent">Ordenar: Recentes</option>
                </select>

                {/* Categorias dinâmicas */}
                {categorias.map(cat => {
                  const selected = filterCategorias[cat.id] || [];
                  const selectedArr = Array.isArray(selected) ? selected : (selected ? [selected] : []);
                  const isOpen = openCatDropdown === cat.id;
                  return (
                    <div key={cat.id} className="relative">
                      <button
                        onClick={() => setOpenCatDropdown(isOpen ? null : cat.id)}
                        className={`w-[200px] px-3 py-2 rounded-lg border text-sm text-left flex items-center justify-between gap-1 ${darkMode ? "bg-zinc-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"} ${selectedArr.length > 0 ? (darkMode ? 'ring-1 ring-blue-500' : 'ring-1 ring-blue-400') : ''}`}
                      >
                        <span className="truncate">{selectedArr.length > 0 ? `${cat.label} (${selectedArr.length})` : cat.label}</span>
                        <span className="text-[10px]">▼</span>
                      </button>
                      {isOpen && (
                        <div className={`absolute top-full left-0 mt-1 z-50 rounded-lg border shadow-lg max-h-60 overflow-y-auto min-w-[180px] ${darkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                          <div className={`flex gap-2 px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <button onClick={() => {
                              const allIds = (cat.opcoes || []).map(o => o.id);
                              setFilterCategorias({ ...filterCategorias, [cat.id]: allIds });
                            }} className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>
                              Todos
                            </button>
                            <button onClick={() => {
                              setFilterCategorias({ ...filterCategorias, [cat.id]: undefined });
                            }} className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                              Limpar
                            </button>
                          </div>
                          <label className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:${darkMode ? 'bg-zinc-700' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <input type="checkbox" checked={selectedArr.includes('__none__')} onChange={() => {
                              const newArr = selectedArr.includes('__none__') ? selectedArr.filter(x => x !== '__none__') : [...selectedArr, '__none__'];
                              setFilterCategorias({ ...filterCategorias, [cat.id]: newArr.length ? newArr : undefined });
                            }} />
                            <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Sem classificação</span>
                          </label>
                          {(cat.opcoes || []).map(opc => (
                            <label key={opc.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                              <input type="checkbox" checked={selectedArr.includes(opc.id)} onChange={() => {
                                const newArr = selectedArr.includes(opc.id) ? selectedArr.filter(x => x !== opc.id) : [...selectedArr, opc.id];
                                setFilterCategorias({ ...filterCategorias, [cat.id]: newArr.length ? newArr : undefined });
                              }} />
                              <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{opc.nome}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Buscar */}
                <button onClick={() => { loadAllGroups(); loadAnalytics(null); }}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-nomoz-orange text-white hover:bg-nomoz-orange-dark whitespace-nowrap flex-shrink-0"
                >
                  Buscar
                </button>

                {/* Limpar */}
                {(searchTerm || filterStatus !== 'active' || filterRole !== 'all' || filterInstance !== 'all' || filterTag !== 'all' || filterCidade !== 'all' || filterDateFrom || filterDateTo || Object.values(filterCategorias).some(v => v && (Array.isArray(v) ? v.length > 0 : true))) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('active');
                      setFilterRole('all');
                      setFilterInstance('all');
                      setFilterTag('all');
                      setFilterCidade('all');
                      setFilterCategorias({});
                      setFilterDateFrom('');
                      setFilterDateTo('');
                    }}
                    className={`px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap ${darkMode ? 'border-red-600 text-red-400 hover:bg-red-900/30' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                  >
                    ✕ Limpar
                  </button>
                )}

                {/* Configurações */}
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className={`ml-auto p-2 rounded-lg border flex-shrink-0 ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
                  title="Configurar categorias e metas"
                >
                  <Settings size={18} />
                </button>
                </div>
              </div>
            </div>

            {/* Groups Grid */}
            {filteredGroups.length === 0 ? (
              <div className={`text-center py-16 rounded-xl ${darkMode ? "bg-zinc-700" : "bg-white"}`}>
                <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className={`text-lg font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Nenhum grupo encontrado
                </h3>
                <p className={`mt-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Clique em "Sincronizar" para buscar grupos do WhatsApp
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedGroups.map((group) => (
                  <div
                    key={group.id}
                    className={`rounded-xl border-2 p-4 transition-all hover:shadow-xl ${darkMode
                        ? "bg-gradient-to-br from-zinc-800 to-zinc-900 border-gray-700 hover:border-nomoz-orange"
                        : "bg-white border-gray-200 hover:border-green-400"
                      } ${(!group.status || !group.phone_number) ? "opacity-50" : ""}`}
                  >
                    {/* Header com foto e nome */}
                    <div className="flex items-start gap-3 mb-3">
                      {group.foto ? (
                        <img
                          src={group.foto}
                          alt={group.nome}
                          className="h-14 w-14 rounded-xl object-cover flex-shrink-0 ring-2 ring-green-500/20"
                        />
                      ) : (
                        <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-lg">
                          {group.nome?.charAt(0)?.toUpperCase() || "G"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-base font-bold truncate ${darkMode ? "text-white" : "text-gray-900"}`}>
                            {group.nome || "Sem nome"}
                          </h3>
                          {group._isNew && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-500 text-white">
                              Novo
                            </span>
                          )}
                          {groupsMessageCount[group.id] > 0 && (
                            <span className="px-2 py-1 text-xs font-bold rounded-full bg-purple-500 text-white animate-pulse">
                              {groupsMessageCount[group.id]} hoje
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                            📱 {getInstanceName(group)}
                          </span>
                        </div>
                        <div className="flex gap-1 mt-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${group.is_admin
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                            }`}>
                            {group.is_admin ? "👑 Admin" : "👤 Membro"}
                          </span>
                          {(!group.status || !group.phone_number) && (
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                              ⚠️ Inativo
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-2 items-center">
                      {(group.tags || []).map((tag, i) => (
                        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-nomoz-orange/20 text-orange-300' : 'bg-orange-50 text-orange-700'
                          }`}>
                          #{tag}
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            const newTags = group.tags.filter((_, idx) => idx !== i);
                            try {
                              await api.patch(`/grupos-whatsapp/${group.id}`, { tags: newTags });
                              setGroups(groups.map(g => g.id === group.id ? { ...g, tags: newTags } : g));
                            } catch (err) { toast.error('Erro ao remover tag'); }
                          }} className="ml-1 hover:text-red-500">×</button>
                        </span>
                      ))}
                      {group._addingTag ? (
                        <input
                          autoFocus
                          type="text"
                          placeholder="tag"
                          className={`w-16 px-2 py-0.5 rounded-full text-xs border outline-none ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              const newTags = [...(group.tags || []), e.target.value.trim().toLowerCase()];
                              try {
                                await api.patch(`/grupos-whatsapp/${group.id}`, { tags: newTags });
                                setGroups(groups.map(g => g.id === group.id ? { ...g, tags: newTags, _addingTag: false } : g));
                              } catch (err) { toast.error('Erro ao adicionar tag'); }
                            }
                            if (e.key === 'Escape') {
                              setGroups(groups.map(g => g.id === group.id ? { ...g, _addingTag: false } : g));
                            }
                          }}
                          onBlur={() => setGroups(groups.map(g => g.id === group.id ? { ...g, _addingTag: false } : g))}
                        />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setGroups(groups.map(g => g.id === group.id ? { ...g, _addingTag: true } : g));
                          }}
                          className={`px-2 py-0.5 rounded-full text-xs border border-dashed transition ${darkMode ? 'border-gray-600 text-gray-500 hover:border-gray-400 hover:text-gray-300' : 'border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600'
                            }`}
                        >
                          +
                        </button>
                      )}
                    </div>

                    {/* Categorias atribuídas */}
                    {group.categorias_valores?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {group.categorias_valores.slice(0, 6).map((cv, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-600'
                            }`}>
                            {cv.opcao_nome}
                          </span>
                        ))}
                        {group.categorias_valores.length > 6 && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{group.categorias_valores.length - 6}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Descrição */}
                    {group.descricao && (
                      <p className={`text-sm mb-3 line-clamp-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                        {group.descricao}
                      </p>
                    )}

                    {/* Stats */}
                    <div className={`p-3 rounded-lg mb-3 ${darkMode ? "bg-zinc-700/50" : "bg-gradient-to-r from-gray-50 to-gray-100"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-green-500" />
                          <div>
                            <span className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                              {group.total_participantes || 0}
                            </span>
                            <span className={`text-[10px] block ${darkMode ? "text-gray-500" : "text-gray-400"}`}>membros</span>
                          </div>
                          {group.growth !== undefined && group.growth !== 0 && (
                            <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${group.growth > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                              {group.growth > 0 ? '+' : ''}{group.growth}
                            </span>
                          )}
                        </div>
                        {group.contatos_unicos > 0 && (
                          <div className="text-right">
                            <span className={`text-sm font-bold ${darkMode ? "text-purple-400" : "text-purple-600"}`}>{group.contatos_unicos}</span>
                            <span className={`text-[10px] block ${darkMode ? "text-gray-500" : "text-gray-400"}`}>exclusivos</span>
                          </div>
                        )}
                      </div>
                      {(group.meta_participantes > 0 || group.responsavel_contato_id) && (
                        <div className={`flex items-center justify-between mt-2 pt-2 border-t ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                          {group.meta_participantes > 0 && (
                            <div className="flex items-center gap-1.5">
                              <div className={`w-16 h-1.5 rounded-full ${darkMode ? 'bg-zinc-600' : 'bg-gray-200'}`}>
                                <div className={`h-1.5 rounded-full ${Math.round((group.total_participantes / group.meta_participantes) * 100) >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, Math.round((group.total_participantes / group.meta_participantes) * 100))}%` }} />
                              </div>
                              <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{Math.round((group.total_participantes / group.meta_participantes) * 100)}%</span>
                            </div>
                          )}
                          {group.responsavel_contato_id && (
                            <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>👤 {group.responsavel_contato_id.slice(0, 8)}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/grupos-wa/${group.id}`)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${darkMode
                            ? "bg-zinc-700 text-white hover:bg-zinc-600"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setInsightsGroupId(group.id)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${darkMode
                            ? "bg-orange-700 text-white hover:bg-orange-600"
                            : "bg-orange-500 text-white hover:bg-orange-600"
                          }`}
                        title="Insights do grupo"
                      >
                        <TrendingUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setWordCloudGroupId(group.id)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${darkMode
                            ? "bg-cyan-700 text-white hover:bg-cyan-600"
                            : "bg-cyan-500 text-white hover:bg-cyan-600"
                          }`}
                        title="Nuvem de palavras"
                      >
                        <Cloud className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); exportParticipantesCSV(group); }}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${darkMode
                            ? "bg-purple-700 text-white hover:bg-purple-600"
                            : "bg-purple-500 text-white hover:bg-purple-600"
                          }`}
                        title="Exportar participantes CSV"
                        style={{ display: isUsuario() ? undefined : 'none' }}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {group.is_admin && group.status && group.phone_number && (
                        <button
                          onClick={() => openAddParticipantsModal(group)}
                          className="px-3 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-sm"
                          title="Adicionar participantes"
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                      )}
                      {group.status && group.phone_number && (
                        <button
                          onClick={() => leaveGroup(group)}
                          className="px-3 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg text-sm font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-sm"
                          title="Sair do grupo"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between mt-6 p-4 rounded-lg ${darkMode ? "bg-zinc-700" : "bg-gray-100"}`}>
                <div className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredGroups.length)} de {filteredGroups.length} grupos
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === 1
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-green-600 hover:text-white"
                      } ${darkMode ? "bg-zinc-600 text-white" : "bg-white text-gray-700"}`}
                  >
                    ← Anterior
                  </button>
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Mostrar apenas páginas próximas
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === page
                                ? "bg-green-600 text-white"
                                : darkMode
                                  ? "bg-zinc-600 text-white hover:bg-zinc-500"
                                  : "bg-white text-gray-700 hover:bg-gray-200"
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className={darkMode ? "text-gray-500" : "text-gray-400"}>...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === totalPages
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-green-600 hover:text-white"
                      } ${darkMode ? "bg-zinc-600 text-white" : "bg-white text-gray-700"}`}
                  >
                    Próxima →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal Criar Grupo */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className={`modal-container max-w-md ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Criar Novo Grupo
                </h3>
                <button onClick={() => setShowCreateModal(false)} className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}>×</button>
              </div>
              <div className="modal-body space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}>Nome do Grupo</label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className={`w-full px-4 py-2 rounded-md border ${darkMode ? "bg-zinc-700 text-white border-gray-600" : "bg-white border-gray-300"}`}
                    placeholder="Nome do grupo"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Participantes
                  </label>

                  {/* Selected Contacts */}
                  {selectedContacts.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {selectedContacts.map(contactId => {
                        const contact = contacts.find(c => c.id === contactId);
                        return contact ? (
                          <span key={contactId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                            {contact.nome}
                            <button
                              type="button"
                              onClick={() => toggleContactSelection(contactId)}
                              className="ml-2 text-green-600 hover:text-green-800"
                            >×</button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setModalMode("create");
                      setContactSearchTerm("");
                      setShowContactModal(true);
                    }}
                    className={`gradient-button w-full px-4 py-2 rounded-md text-sm font-medium`}
                  >
                    <Plus className="h-4 w-4 mr-2 inline" />
                    {selectedContacts.length === 0 ? "Selecionar Contatos" : `Gerenciar Contatos (${selectedContacts.length})`}
                  </button>
                  <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {selectedContacts.length} contato(s) selecionado(s)
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowCreateModal(false)} className={`modal-button modal-button-secondary ${darkMode ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}>Cancelar</button>
                <button onClick={createGroup} disabled={selectedContacts.length === 0} className="modal-button gradient-button disabled:opacity-50">Criar Grupo</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Selecionar Contatos */}
        {showContactModal && (
          <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
            <div className={`modal-container ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  {modalMode === "create" ? "Selecionar Participantes" : `Adicionar a ${selectedGroup?.nome}`}
                </h3>
                <div className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {selectedContacts.length} selecionado(s)
                </div>
              </div>

              <div className="modal-body space-y-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar contatos..."
                    className={`w-full px-4 py-2 rounded-md border ${darkMode ? "bg-zinc-700 text-white border-gray-600 placeholder-gray-400" : "bg-white border-gray-300 placeholder-gray-500"}`}
                    value={contactSearchTerm}
                    onChange={(e) => setContactSearchTerm(e.target.value)}
                  />
                </div>

                {/* Selection controls */}
                <div className={`flex justify-between items-center p-2 rounded ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                  <div className="flex space-x-2">
                    <button onClick={selectAllContacts} className="text-sm text-green-500 hover:text-green-700">
                      Selecionar Todos
                    </button>
                    <span className={darkMode ? "text-gray-500" : "text-gray-400"}>|</span>
                    <button onClick={clearSelection} className="text-sm text-red-500 hover:text-red-700">
                      Limpar Seleção
                    </button>
                  </div>
                  <div className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {filteredContactsForModal.length} contato(s) disponível(is)
                  </div>
                </div>

                {/* Contacts list */}
                <div className={`max-h-80 overflow-y-auto border rounded ${darkMode ? "bg-zinc-700 border-gray-600" : "bg-white border-gray-300"}`}>
                  {filteredContactsForModal.length === 0 ? (
                    <div className={`p-4 text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Nenhum contato encontrado
                    </div>
                  ) : (
                    filteredContactsForModal.map(contact => (
                      <div
                        key={contact.id}
                        className={`flex items-center p-3 border-b cursor-pointer hover:bg-opacity-50 ${darkMode ? "border-gray-600 hover:bg-zinc-600" : "border-gray-200 hover:bg-gray-50"
                          } ${selectedContacts.includes(contact.id) ? (darkMode ? "bg-zinc-600" : "bg-green-50") : ""}`}
                        onClick={() => toggleContactSelection(contact.id)}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded accent-green-600"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => { }}
                          style={{ cursor: "pointer" }}
                        />
                        <div className="ml-3 flex-1">
                          <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                            {contact.nome}
                          </p>
                          <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {contact.telefone}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => setShowContactModal(false)}
                  className={`modal-button modal-button-secondary ${darkMode ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (modalMode === "add") {
                      addParticipants();
                    } else {
                      setShowContactModal(false);
                    }
                  }}
                  disabled={selectedContacts.length === 0}
                  className="modal-button gradient-button disabled:opacity-50"
                >
                  {modalMode === "add" ? "Adicionar" : "Confirmar"} {selectedContacts.length > 0 ? `(${selectedContacts.length})` : ""}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detalhes */}
        {showDetailsModal && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className={`modal-container max-w-2xl ${darkMode ? "bg-zinc-800" : "bg-white"}`} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Detalhes do Grupo
                </h3>
                <button onClick={() => setShowDetailsModal(false)} className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}>×</button>
              </div>

              {!groupDetails ? (
                <div className="modal-body flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nomoz-orange"></div>
                </div>
              ) : (
                <div className="modal-body space-y-4">
                  {/* Header com foto */}
                  <div className="flex items-center gap-4">
                    {groupDetails.foto ? (
                      <img
                        src={groupDetails.foto}
                        alt={groupDetails.nome}
                        className="h-20 w-20 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-lg bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-3xl font-bold">
                        {groupDetails.nome?.charAt(0)?.toUpperCase() || "G"}
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                        {groupDetails.nome}
                      </h4>
                      {groupDetails.descricao && (
                        <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          {groupDetails.descricao}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`p-4 rounded-lg text-center ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                      <p className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                        {groupDetails.total_participantes}
                      </p>
                      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Participantes</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                      <p className={`text-2xl font-bold ${groupDetails.is_admin ? "text-green-500" : "text-orange-500"}`}>
                        {groupDetails.is_admin ? "Sim" : "Não"}
                      </p>
                      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Admin</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                      <p className={`text-2xl font-bold text-blue-500`}>
                        {groupDetails.metadata?.participants?.filter(p => p.isAdmin || p.isSuperAdmin).length || 0}
                      </p>
                      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Admins</p>
                    </div>
                  </div>

                  {/* Responsável e Meta */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Responsável */}
                    <div className={`p-3 rounded-lg border ${darkMode ? "border-gray-700 bg-zinc-700/50" : "border-gray-200 bg-gray-50"}`}>
                      <label className={`text-xs font-semibold block mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                        👤 Responsável
                      </label>
                      <select
                        value={groupDetails.responsavel_contato_id || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await waGroupsAPI.setResponsavel(groupDetails.id, val);
                            setGroupDetails({ ...groupDetails, responsavel_contato_id: val });
                            toast.success("Responsável atualizado");
                          } catch (err) { toast.error("Erro ao salvar"); }
                        }}
                        className={`w-full px-2 py-1.5 rounded border text-sm ${darkMode ? "bg-zinc-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                      >
                        <option value="">Nenhum</option>
                        {(groupDetails.metadata?.participants || []).map((p, i) => (
                          <option key={i} value={p.phone}>
                            {p.name || p.phone}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Meta */}
                    <div className={`p-3 rounded-lg border ${darkMode ? "border-gray-700 bg-zinc-700/50" : "border-gray-200 bg-gray-50"}`}>
                      <label className={`text-xs font-semibold block mb-1.5 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                        🎯 Meta de Participantes
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          defaultValue={groupDetails.meta_participantes || 0}
                          onBlur={async (e) => {
                            const val = Number(e.target.value);
                            try {
                              await waGroupsAPI.setMeta(groupDetails.id, val);
                              setGroupDetails({ ...groupDetails, meta_participantes: val });
                              toast.success("Meta salva");
                            } catch (err) { toast.error("Erro ao salvar"); }
                          }}
                          className={`w-20 px-2 py-1.5 rounded border text-sm ${darkMode ? "bg-zinc-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                            }`}
                        />
                        <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                          Atual: {groupDetails.total_participantes}
                          {groupDetails.meta_participantes > 0 && (
                            <> ({Math.round((groupDetails.total_participantes / groupDetails.meta_participantes) * 100)}%)</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Participantes */}
                  {groupDetails.metadata?.participants && (
                    <div>
                      <h5 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                        Participantes ({groupDetails.metadata.participants.length})
                      </h5>
                      <div className={`max-h-64 overflow-y-auto border rounded-lg ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                        {groupDetails.metadata.participants.map((participant, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-3 border-b last:border-b-0 ${darkMode ? "border-gray-700" : "border-gray-200"
                              }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${participant.isSuperAdmin ? "bg-red-500" :
                                  participant.isAdmin ? "bg-green-500" :
                                    "bg-gray-500"
                                }`}>
                                {participant.name?.charAt(0)?.toUpperCase() || participant.phone?.charAt(0) || "?"}
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                                  {participant.name || participant.phone || "Sem nome"}
                                </p>
                                <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                  {participant.phone}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 items-center">
                              {participant.isSuperAdmin && (
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
                                  Super Admin
                                </span>
                              )}
                              {participant.isAdmin && !participant.isSuperAdmin && (
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
                                  Admin
                                </span>
                              )}
                              {groupDetails.is_admin && !participant.isSuperAdmin && (
                                <button
                                  onClick={() => {
                                    showConfirm({
                                      title: 'Remover Participante',
                                      message: `Remover ${participant.name || participant.phone} do grupo?`,
                                      confirmText: 'Remover',
                                      type: 'danger',
                                      onConfirm: async () => {
                                        try {
                                          await api.delete(`/grupos-whatsapp/${groupDetails.id}/participants/${participant.phone}?cliente_id=${clienteId}`);
                                          toast.success('Participante removido');
                                          viewDetails(groupDetails);
                                        } catch { toast.error('Erro ao remover'); }
                                      }
                                    });
                                  }}
                                  className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                                  title="Remover do grupo"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ID do Grupo */}
                  <div className={`p-3 rounded-lg ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"} mb-1`}>ID do Grupo</p>
                    <p className={`font-mono text-xs ${darkMode ? "text-white" : "text-gray-900"} break-all`}>
                      {groupDetails.group_id}
                    </p>
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`modal-button modal-button-secondary ${darkMode ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>


      {insightsGroupId && <GroupInsights groupId={insightsGroupId} onClose={() => setInsightsGroupId(null)} />}
      {wordCloudGroupId && <GroupWordCloud groupId={wordCloudGroupId} onClose={() => setWordCloudGroupId(null)} />}
      <GroupSettingsModal
        isOpen={showSettingsModal}
        onClose={() => { setShowSettingsModal(false); loadCategorias(); }}
        clienteId={clienteId}
        metaGeral={metaGeral}
        onMetaGeralChange={setMetaGeral}
      />
    </Layout>
  );
};

export default WhatsappGroups;
